import { useEffect, useMemo, useState } from 'react'
import { Check, ChevronDown, History, Search } from 'lucide-react'
import { Popover } from '@/components/ds2'
import { listGeneralChats } from '../api/generalAgentChatApi'
import type { GeneralChatSummary } from '../types'
import { chatTitleText } from './uploads/untrustedText'

type HistoryState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; chats: GeneralChatSummary[] }

/** Past this many chats the panel offers a search field. */
const SEARCH_FROM = 8

const TIME_FORMAT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
const DAY_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const DAY_YEAR_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const lastActive = (chat: GeneralChatSummary) => new Date(chat.last_activity_at ?? chat.updated_at ?? chat.created_at)

const titleOf = (chat: GeneralChatSummary) => chatTitleText(chat.title ?? '') || 'Untitled chat'

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

const DAY_MS = 24 * 60 * 60 * 1000

/** The group a chat is listed under, by its last activity against today. */
const groupOf = (date: Date, today: number) => {
    const day = startOfDay(date)
    if (day >= today) return 'Today'
    if (day >= today - DAY_MS) return 'Yesterday'
    if (day >= today - 7 * DAY_MS) return 'Previous 7 days'
    return 'Older'
}

/** Today's and yesterday's chats show their time (the group already names the day, and the time tells same-titled chats apart); older ones their date. */
const whenOf = (date: Date, today: number, thisYear: number) =>
    startOfDay(date) >= today - DAY_MS ? TIME_FORMAT.format(date) : (date.getFullYear() === thisYear ? DAY_FORMAT : DAY_YEAR_FORMAT).format(date)

interface GeneralAgentHistoryProps {
    /** The open chat, marked in the list when it is there. */
    currentTabId: string | null
    /** Opens a past chat. */
    onOpen: (tabId: string) => void
}

/** The chat history of the invoicing chat's top bar: a button that opens past chats, newest first, grouped by day. */
export const GeneralAgentHistory = ({ currentTabId, onOpen }: GeneralAgentHistoryProps) => {
    const [history, setHistory] = useState<HistoryState>({ status: 'loading' })
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')

    // Read again whenever the open chat changes, so a chat started just now is listed.
    useEffect(() => {
        let cancelled = false
        listGeneralChats()
            .then((chats) => !cancelled && setHistory({ status: 'ready', chats }))
            .catch(() => !cancelled && setHistory({ status: 'error' }))
        return () => {
            cancelled = true
        }
    }, [currentTabId])

    const chats = useMemo(() => (history.status === 'ready' ? history.chats : []), [history])

    const groups = useMemo(() => {
        const now = new Date()
        const today = startOfDay(now)
        const needle = query.trim().toLowerCase()
        const byGroup = new Map<string, { chat: GeneralChatSummary; title: string; when: string }[]>()
        for (const chat of chats) {
            const title = titleOf(chat)
            if (needle && !title.toLowerCase().includes(needle)) continue
            const date = lastActive(chat)
            const group = groupOf(date, today)
            byGroup.set(group, [...(byGroup.get(group) ?? []), { chat, title, when: whenOf(date, today, now.getFullYear()) }])
        }
        return [...byGroup.entries()]
    }, [chats, query])

    const pick = (id: string) => {
        setOpen(false)
        setQuery('')
        if (id !== currentTabId) onOpen(id)
    }

    const trigger = (
        <span className="inline-flex h-8 items-center gap-1.5 rounded-3xl px-3 text-sm font-medium text-foreground transition-colors duration-150 hover:bg-default">
            <History className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
            History
            {chats.length > 0 && <span className="text-xs font-normal text-foreground-muted">{chats.length}</span>}
            <ChevronDown className={`h-3.5 w-3.5 text-foreground-muted transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden />
        </span>
    )

    return (
        <Popover
            trigger={
                <button type="button" aria-label="Chat history" className="rounded-3xl outline-none focus-visible:ring-2 focus-visible:ring-accent">
                    {trigger}
                </button>
            }
            ariaLabel="Chat history"
            open={open}
            onOpenChange={(next) => {
                setOpen(next)
                if (!next) setQuery('')
            }}
            portal
            panelClassName="w-[340px] max-w-[calc(100vw-16px)] gap-0 rounded-2xl p-2"
        >
            {chats.length > SEARCH_FROM && (
                <label className="mb-2 flex h-9 items-center gap-2 rounded-xl bg-default px-3">
                    <Search className="h-3.5 w-3.5 shrink-0 text-foreground-muted" aria-hidden />
                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search chats"
                        aria-label="Search chats"
                        className="w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted"
                    />
                </label>
            )}
            <div className="max-h-[400px] overflow-y-auto">
                {history.status === 'loading' && <p className="px-3 py-6 text-center text-sm text-foreground-muted">Loading chats…</p>}
                {history.status === 'error' && <p className="px-3 py-6 text-center text-sm text-foreground-muted">Chat history is unavailable right now.</p>}
                {history.status === 'ready' && chats.length === 0 && (
                    <p className="px-3 py-6 text-center text-sm text-foreground-muted">No past chats yet. Your chats will be listed here.</p>
                )}
                {history.status === 'ready' && chats.length > 0 && groups.length === 0 && (
                    <p className="px-3 py-6 text-center text-sm text-foreground-muted">No chat matches “{query.trim()}”.</p>
                )}
                {groups.map(([group, rows]) => (
                    <section key={group} aria-label={group} className="pb-1">
                        <h3 className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">{group}</h3>
                        <ul className="m-0 list-none p-0">
                            {rows.map(({ chat, title, when }) => {
                                const current = chat.id === currentTabId
                                return (
                                    <li key={chat.id}>
                                        <button
                                            type="button"
                                            onClick={() => pick(chat.id)}
                                            aria-current={current ? 'true' : undefined}
                                            title={title}
                                            className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                                current ? 'bg-accent-soft text-accent-soft-foreground' : 'text-foreground hover:bg-default'
                                            }`}
                                        >
                                            <span className="min-w-0 flex-1 truncate">{title}</span>
                                            <span className={`shrink-0 text-xs ${current ? '' : 'text-foreground-muted'}`}>{when}</span>
                                            {current && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                                        </button>
                                    </li>
                                )
                            })}
                        </ul>
                    </section>
                ))}
            </div>
        </Popover>
    )
}
